import { IS_CLIENT } from './support';
import { isFunction, isNil, isString, isUndefined } from './unit';
/**
 * Requests an animation frame and waits for it to be resolved.
 *
 * @param callback - Invoked on the next animation frame.
 */
export function raf(callback) {
    return new Promise((resolve) => {
        const rafId = window.requestAnimationFrame(async () => {
            await (callback === null || callback === void 0 ? void 0 : callback());
            resolve(rafId);
        });
    });
}
/**
 * Builds an `exportsparts` attribute value given an array of `parts` and an optional `prefix`.
 *
 * @param parts
 * @param prefix
 */
export function buildExportPartsAttr(parts, prefix) {
    return parts
        .map((part) => `${part}: ${!isFunction(prefix) ? (prefix ? `${prefix}-` : '') : prefix(part)}${part}`)
        .join(', ');
}
/**
 * Registers a custom element in the CustomElementRegistry. By "safely" we mean:
 *
 * - Called only client-side (`window` is defined).
 * - The element is only registered if it hasn't been registered before under the given `name`.
 *
 * @param name - A string representing the name you are giving the element.
 * @param constructor - A class object that defines the behaviour of the element.
 * @param isClient
 */
export function safelyDefineCustomElement(name, constructor, isClient = IS_CLIENT) {
    const isElementRegistered = isClient && !isUndefined(window.customElements.get(name));
    if (!isClient || isElementRegistered)
        return;
    window.customElements.define(name, constructor);
}
/**
 * Sets an attribute on the given `el`. If the `attrValue` is `undefined`or `null` the attribute
 * will be removed.
 *
 * @param element - The element to set the attribute on.
 * @param attrName - The name of the attribute.
 * @param attrValue - The value of the attribute.
 */
export function setAttribute(element, attrName, attrValue) {
    if (isNil(attrValue) || attrValue === false) {
        element.removeAttribute(attrName);
    }
    else {
        element.setAttribute(attrName, isString(attrValue) ? attrValue : '');
    }
}
/**
 * Returns elements assigned to the given slot in the shadow root. Filters out all nodes
 * which are not of type `Node.ELEMENT_NODE`.
 *
 * @param el - The element containing the slot.
 * @param name - The name of the slot (optional).
 */
export function getSlottedChildren(el, name) {
    var _a, _b;
    const selector = name ? `slot[name="${name}"]` : 'slot:not([name])';
    const slot = (_a = el.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(selector);
    const childNodes = (_b = slot === null || slot === void 0 ? void 0 : slot.assignedNodes({ flatten: true })) !== null && _b !== void 0 ? _b : [];
    return Array.prototype.filter.call(childNodes, (node) => node.nodeType == Node.ELEMENT_NODE);
}
export function getElementAttributes(elementCtor) {
    return new Set(elementCtor.observedAttributes);
}
export function observeAttributes(element, attributes, onChange) {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'attributes') {
                const attrName = mutation.attributeName;
                const attrValue = element.getAttribute(attrName);
                onChange === null || onChange === void 0 ? void 0 : onChange(attrName, attrValue);
            }
        }
    });
    observer.observe(element, {
        attributeFilter: Array.from(attributes)
    });
    return observer;
}
